import { useEffect, useMemo, useRef, useState } from 'react';
import ffinUploadCarPhoto from '../../../../api/bank/ffinUploadCarPhoto';
import alert from '../../../../helpers/alert';
import { BANKS } from '../../../../constants';
import { SLOTS, slotsFor } from './carPhotoSlots';

// Фото авто для оценки залога (комиссионное / б/у авто). Нужны и Freedom, и
// Jet Finance: загружаются в одно место, документы сделки, а дальше каждый банк
// забирает их по-своему — Freedom при выборе банка (до /confirm), Jet Finance
// после одобрения скоринга.
// Какие ракурсы кому нужны — в carPhotoSlots.js.
const CommissionPhotos = ({ appealId, documents, onReadyChange, banks = [], newCar = false }) => {
  const slots = useMemo(() => slotsFor(banks, { newCar }), [banks, newCar]);
  const forJetFin = banks.includes(BANKS.JETFIN);
  // Какие ракурсы уже загружены (по документам сделки) — чтобы не терять при перезагрузке.
  const initialDone = useMemo(() => {
    const done = {};
    (documents || []).forEach((d) => {
      SLOTS.forEach((s) => {
        if (d?.typeId === s.typeId && (d?.serialNumberId ?? 1) === s.serial) {
          done[s.key] = true;
        }
      });
    });
    return done;
  }, [documents]);

  const [done, setDone] = useState(initialDone);
  const [busy, setBusy] = useState(null);
  const inputs = useRef({});

  // Именно по показанным: требовать ракурс, которого на экране нет, значит
  // запереть менеджера в блоке без возможности его закрыть.
  const requiredKeys = slots.filter((s) => s.required).map((s) => s.key);
  const ready = requiredKeys.every((k) => done[k]);

  useEffect(() => {
    onReadyChange && onReadyChange(ready);
  }, [ready, onReadyChange]);

  const pick = (slot) => inputs.current[slot.key]?.click();

  const onFile = async (slot, e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!appealId) {
      alert.error('Заявка Freedom ещё не создана — фото загрузить нельзя');
      return;
    }
    setBusy(slot.key);
    try {
      await ffinUploadCarPhoto({ appealId, documentType: slot.key, file });
      setDone((prev) => ({ ...prev, [slot.key]: true }));
      alert.success(`Фото «${slot.label}» загружено`);
    } catch (err) {
      alert.error(err?.response?.data?.errorMessage || 'Ошибка загрузки фото');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div
      style={{
        border: '1px solid #e0e0e0',
        borderRadius: 12,
        padding: 16,
        marginBottom: 20,
        background: ready ? '#f2fbf3' : '#fff8f0',
      }}
    >
      <div style={{ fontWeight: 600, marginBottom: 4 }}>
        {newCar ? 'Фото VIN для банка' : 'Фото авто для оценки залога'}
      </div>
      <div style={{ fontSize: 13, color: '#666', marginBottom: 12 }}>
        {newCar ? (
          'Новое авто из салона. Jet Finance ждёт два снимка VIN: выбитый на кузове и наклейку — их на машине два. Загрузите после одобрения, в банк они уйдут сами.'
        ) : (
          <>
            Комиссионное / б/у авто. Пять фото со звёздочкой обязательны: без них Freedom отклонит заявку, и загрузить
            их нужно до выбора банка.
            {forJetFin ? ' Остальные ракурсы идут в оценку залога Jet Finance — чем больше, тем точнее.' : ''}
          </>
        )}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
        {slots.map((slot) => (
          <div
            key={slot.key}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              minWidth: 150,
            }}
          >
            <button
              type="button"
              onClick={() => pick(slot)}
              disabled={busy === slot.key}
              style={{
                cursor: 'pointer',
                padding: '8px 12px',
                borderRadius: 8,
                border: '1px solid #c8c8c8',
                background: done[slot.key] ? '#e3f5e6' : '#fff',
                width: '100%',
                textAlign: 'left',
              }}
            >
              {done[slot.key] ? '✓ ' : ''}
              {slot.label}
              {slot.required ? ' *' : ''}
              {busy === slot.key ? ' …' : ''}
            </button>
            {slot.hint ? (
              <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>{slot.hint}</div>
            ) : null}
            <input
              ref={(el) => (inputs.current[slot.key] = el)}
              type="file"
              accept="image/png,image/jpeg"
              style={{ display: 'none' }}
              onChange={(e) => onFile(slot, e)}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

export default CommissionPhotos;
